/**
 * プロジェクトステータス（表示用）
 */
export type ProjectStatus = '進行中' | '完了' | '保留' | '中止';

/**
 * プロジェクトステータス（データベース用）
 */
export interface ProjectStatusData {
    id: string;
    code: string;
    label: string;
    sort_order: number;
}

/**
 * 建築種別（データベース用）
 */
export interface BuildingTypeData {
    id: string;
    code: string;
    label: string;
}

/**
 * プロジェクト（バックエンドのProjectResponseに合わせる）
 */
export interface Project {
    id: string;
    name: string;
    address?: string | null;
    building_type?: string | null;
    status?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    notes?: string | null;
    created_at: string;
    updated_at: string;
}

/**
 * プロジェクトレスポンス（APIから受信するデータ）
 */
export interface ProjectResponse {
    id: string;
    name: string;
    address?: string | null;
    building_type?: string | null;
    status?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    notes?: string | null;
    created_at: string;
    updated_at: string;
}

/**
 * リストレスポンス
 */
export interface ListResponse<T> {
    items: T[];
    total: number;
}

/**
 * プロジェクトリストレスポンス
 */
export type ProjectListResponse = ListResponse<Project>;

/**
 * プロジェクト作成用データ（バックエンドのProjectCreateスキーマに合わせる）
 */
export interface ProjectInsert {
    name: string;
    address: string;
    building_type?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    notes?: string | null;
    status?: string | null;
}

/**
 * プロジェクト更新用データ
 */
export interface ProjectUpdate {
    name?: string;
    address?: string | null;
    building_type?: string | null;
    building_type_id?: string | null;
    status?: string | null;
    status_id?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    notes?: string | null;
    updated_by: string;
}

/**
 * プロジェクト作成ペイロード（互換性用）
 */
export interface ProjectCreatePayload {
    name: string;
    address?: string;
}

/**
 * プロジェクト更新ペイロード（互換性用）
 */
export interface ProjectUpdatePayload {
    name?: string;
    address?: string;
    building_type?: string;
    status?: ProjectStatus;
    notes?: string;
}

/**
 * プロジェクトゾーン
 */
export interface ProjectZone {
    id: string;
    tenant_id: string;
    project_id: string;
    name: string;
    area_sqm?: number | null;
    notes?: string | null;
    created_at: string;
    updated_at: string;
    created_by?: string | null;
    updated_by?: string | null;

    // 互換性フィールド
    zone_name?: string;
    area?: number | null;
}

/**
 * プロジェクトゾーン付きプロジェクト
 */
export interface ProjectWithZones extends Project {
    project_zones: ProjectZone[];
}

/**
 * 建築種別の型（表示用）
 */
export type BuildingType =
    | 'COMMERCIAL'
    | 'RESIDENTIAL'
    | 'OFFICE'
    | 'RESIDENTIAL_CONDOMINIUM'
    | 'RESIDENTIAL_DETACHED'
    | 'INDUSTRIAL'
    | '商業施設'
    | '住宅'
    | 'オフィス'
    | '分譲マンション'
    | '戸建住宅'
    | '工場・倉庫';

/**
 * 建築種別のマッピング
 */
export const buildingTypeMap: Record<BuildingType, string> = {
    COMMERCIAL: '商業施設',
    RESIDENTIAL: '住宅',
    OFFICE: 'オフィス',
    RESIDENTIAL_CONDOMINIUM: '分譲マンション',
    RESIDENTIAL_DETACHED: '戸建住宅',
    INDUSTRIAL: '工場・倉庫',
    // データベースに直接保存されている日本語値もサポート
    '商業施設': '商業施設',
    '住宅': '住宅',
    'オフィス': 'オフィス',
    '分譲マンション': '分譲マンション',
    '戸建住宅': '戸建住宅',
    '工場・倉庫': '工場・倉庫',
};

/**
 * プロジェクトのパラメータ
 */
export interface GetProjectsParams {
    skip?: number;
    limit?: number;
    status?: string | string[];
    name?: string;
    address?: string;
    building_type?: string;
    start_date_from?: string;
    start_date_to?: string;
    end_date_from?: string;
    end_date_to?: string;
    area_min?: number;
    area_max?: number;
    include_zones?: boolean;
}

// ================================
// 日次活動関連の型定義
// ================================

/**
 * 日次活動
 */
export interface DailyActivity {
    id: string;
    tenant_id: string;
    project_id: string;
    zone_id?: string | null;
    schedule_date: string;
    notes?: string | null;
    created_at: string;
    updated_at: string;
    created_by?: string | null;
    updated_by?: string | null;
}

/**
 * 日次活動作成用データ
 */
export interface DailyActivityInsert {
    tenant_id: string;
    project_id: string;
    zone_id?: string | null;
    schedule_date: string;
    notes?: string | null;
    created_by: string;
    updated_by: string;
}

/**
 * 日次活動更新用データ
 */
export interface DailyActivityUpdate {
    zone_id?: string | null;
    schedule_date?: string;
    notes?: string | null;
    updated_by: string;
}

// ================================
// 割り当て関連の型定義
// ================================

/**
 * 職人割り当ての基底型
 */
export interface CraftsmanAssignmentBase {
    activity_id: string;
    craftsman_id: string | null;
    vehicle_id?: string | null;
    notes?: string | null;
}

/**
 * 職人割り当ての作成型
 */
export interface CraftsmanAssignmentInsert extends CraftsmanAssignmentBase {
    daily_rate_at_assignment?: number;
    created_by: string;
    updated_by: string;
}

/**
 * 職人割り当ての更新型
 */
export interface CraftsmanAssignmentUpdate {
    craftsman_id?: string | null;
    vehicle_id?: string | null;
    notes?: string | null;
    daily_rate_at_assignment?: number;
    updated_by: string;
}

/**
 * 職人割り当て（完全版）
 */
export interface CraftsmanAssignment extends CraftsmanAssignmentBase {
    id: string;
    daily_rate_at_assignment?: number | null;
    created_at: string;
    updated_at: string;
    created_by?: string | null;
    updated_by?: string | null;
    craftsman_name?: string;
    // 展開されたオブジェクト（APIから取得時に含まれる）
    craftsman?: {
        id: string;
        name: string;
        [key: string]: any;
    };
    company_vehicle_name?: string;
    company_vehicle?: {
        id: string;
        name: string;
        [key: string]: any;
    };
}

/**
 * トラック割り当ての基底型
 */
export interface TruckAssignmentBase {
    activity_id: string;
    truck_id: string | null;
    driver_id?: string | null;
    notes?: string | null;
}

/**
 * トラック割り当ての作成型
 */
export interface TruckAssignmentInsert extends TruckAssignmentBase {
    daily_rate_at_assignment?: number;
    created_by: string;
    updated_by: string;
}

/**
 * トラック割り当ての更新型
 */
export interface TruckAssignmentUpdate {
    truck_id?: string | null;
    driver_id?: string | null;
    notes?: string | null;
    daily_rate_at_assignment?: number;
    updated_by: string;
}

/**
 * トラック割り当て（完全版）
 */
export interface TruckAssignment extends TruckAssignmentBase {
    id: string;
    daily_rate_at_assignment?: number | null;
    created_at: string;
    updated_at: string;
    created_by?: string | null;
    updated_by?: string | null;
    truck_number?: string;
    driver_name?: string;
    // 展開されたオブジェクト（APIから取得時に含まれる）
    truck?: {
        id: string;
        number: string;
        name?: string;
        [key: string]: any;
    };
    truck_driver?: {
        id: string;
        name: string;
        [key: string]: any;
    };
}

/**
 * 割り当て付き日次活動
 */
export interface DailyActivityWithAssignments extends DailyActivity {
    craftsman_assignments: CraftsmanAssignment[];
    truck_assignments: TruckAssignment[];
    zone_name?: string;
    project_zone?: {
        id: string;
        name: string;
        zone_name?: string;
    };
}
